import { nextTick } from 'vue'
import { useCanvasStore } from '../stores/useCanvasStore'

const IMAGE_READY_TIMEOUT_MS = 5000
const IMAGE_READY_POLL_MS = 30

/**
 * Stage 内の各画像オブジェクト (`#node-<id>` 配下の Konva.Image) が
 * 画像を保持済みになるまで polling で待つ。最大 timeout まで。
 *
 * DrawableImage は `loadImage()` の async 解決後に `imgEl` をセットして
 * 初めて `<v-image>` を描画する。stage の描画準備が「ラベル領域は
 * 出来ているが画像未ロード」状態のまま toDataURL が呼ばれると、
 * その画像オブジェクトが出力に含まれず「真っ白」な書き出しになる。
 */
async function waitForImagesReady(stage, expectedIds) {
    if (expectedIds.length === 0) return
    const start = Date.now()
    while (Date.now() - start < IMAGE_READY_TIMEOUT_MS) {
        const allReady = expectedIds.every(id => {
            const group = stage.findOne('#node-' + id)
            if (!group) return false
            const imageNode = group.findOne('Image')
            return !!(imageNode && imageNode.image())
        })
        if (allReady) return
        await new Promise(r => setTimeout(r, IMAGE_READY_POLL_MS))
    }
}

function loadImageElement(src) {
    return new Promise((resolve, reject) => {
        const img = new window.Image()
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = src
    })
}

async function applyCircularMask(dataUrl, width, height) {
    const img = await loadImageElement(dataUrl)
    const offscreen = document.createElement('canvas')
    offscreen.width = width
    offscreen.height = height
    const ctx = offscreen.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
    ctx.drawImage(img, 0, 0, width, height)
    ctx.globalCompositeOperation = 'destination-in'
    ctx.beginPath()
    ctx.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, Math.PI * 2)
    ctx.fill()
    return offscreen.toDataURL('image/png')
}

/**
 * Konva Stage からラベルの DataURL を生成する。
 * stage = labelRegion と同サイズなので crop は不要。
 * pixelRatio = 1/displayScale により、displayScale 縮小表示でも
 * フル解像度で書き出される。
 *
 * @param {Object} stageRef - DecoCanvas の stageRef
 * @returns {Promise<string>} DataURL (PNG)
 */
export async function exportLabelImage(stageRef) {
    const canvasStore = useCanvasStore()
    const { labelRegion, isCircularLabel } = canvasStore

    canvasStore.deselectAll()

    const stage = stageRef.value?.getNode()
    if (!stage) throw new Error('Stage not found')

    // deselectAll → DecoCanvas の watch (transformer.nodes([])) を反映させる
    await nextTick()

    // 画像オブジェクトのロード完了を待つ
    const imageIds = canvasStore.objects
        .filter(o => o.objectType !== 'text')
        .map(o => o.id)
    await waitForImagesReady(stage, imageIds)

    // オーバーレイレイヤー(3番目: 円形ガイドリング)を一時非表示
    const overlayLayer = stage.children[2]
    overlayLayer?.hide()
    const dataUrl = stage.toDataURL({ pixelRatio: 1 / canvasStore.displayScale })
    overlayLayer?.show()

    if (!isCircularLabel) return dataUrl
    return await applyCircularMask(dataUrl, labelRegion.width, labelRegion.height)
}
